import { useState, useEffect } from 'react';
import { User, CreditCard, ExternalLink, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import Header from '../components/layout/Header';
import { useAuth } from '../contexts/AuthContext';
import { userApi } from '../services/api';
import type { SubscriptionInfo } from '../types';

export default function AccountPage() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadSubscription = async () => {
      try {
        const info = await userApi.getSubscription();
        setSubscription(info);
      } catch (error) {
        console.error('Failed to load subscription:', error);
      } finally {
        setLoading(false);
      }
    };
    loadSubscription();
  }, []);

  if (loading) {
    return (
      <>
        <Header title="Account" subtitle="Manage your subscription and account settings" />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="animate-spin text-teal" size={32} />
        </div>
      </>
    );
  }

  const isActive = user?.user.subscription_status === 'active';

  return (
    <>
      <Header title="Account" subtitle="Manage your subscription and account settings" />

      <div className="flex-1 overflow-auto p-6 space-y-6">
        {/* Account Info */}
        <div className="card max-w-2xl">
          <div className="flex items-center gap-3 mb-6">
            <User className="text-teal" size={24} />
            <h3 className="text-lg font-tech font-semibold">Account Information</h3>
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center py-3 border-b border-teal/20">
              <span className="text-gray-400">Email</span>
              <span className="font-mono">{user?.user.email}</span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-teal/20">
              <span className="text-gray-400">Member Since</span>
              <span>
                {user?.user.created_at
                  ? new Date(user.user.created_at).toLocaleDateString()
                  : '-'}
              </span>
            </div>
            <div className="flex justify-between items-center py-3">
              <span className="text-gray-400">Total Reports Generated</span>
              <span className="font-tech text-lg">{user?.stats.total_reports || 0}</span>
            </div>
          </div>
        </div>

        {/* Subscription */}
        <div className="card max-w-2xl">
          <div className="flex items-center gap-3 mb-6">
            <CreditCard className="text-teal" size={24} />
            <h3 className="text-lg font-tech font-semibold">Subscription</h3>
          </div>

          <div className="flex items-start gap-6">
            <div
              className={`p-4 rounded-lg border-2 flex-1 ${
                isActive
                  ? 'border-signal-green bg-signal-green/10'
                  : 'border-gray-600 bg-gray-800/50'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                {isActive ? (
                  <CheckCircle className="text-signal-green" size={20} />
                ) : (
                  <XCircle className="text-gray-500" size={20} />
                )}
                <span className="font-tech font-semibold text-lg">
                  {isActive ? 'Pro Plan' : 'No Active Plan'}
                </span>
              </div>
              <p className="text-sm text-gray-400">
                {isActive
                  ? 'Unlimited scans and reports with full white-label branding'
                  : 'Subscribe to generate unlimited reports'}
              </p>
            </div>

            <div className="text-right">
              <p className="text-3xl font-tech font-bold text-teal">$29</p>
              <p className="text-sm text-gray-500">per month</p>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-teal/20 flex gap-4">
            {isActive ? (
              subscription?.manage_url && (
                <a
                  href={subscription.manage_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary flex items-center gap-2"
                >
                  <ExternalLink size={18} />
                  Manage Subscription
                </a>
              )
            ) : (
              <a
                href={subscription?.upgrade_url || 'https://shop.velosent.com'}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary flex items-center gap-2"
              >
                <CreditCard size={18} />
                Subscribe Now
              </a>
            )}
          </div>
        </div>

        {/* Plan Features */}
        <div className="card max-w-2xl">
          <h3 className="text-lg font-tech font-semibold mb-4">Pro Plan Features</h3>
          <ul className="space-y-3">
            {[
              'Unlimited domain scans',
              'Unlimited PDF report generation',
              'Full white-label branding (logo, colors, CTA)',
              'Risk scoring algorithm with severity breakdown',
              'Breach source identification (Database dumps, Stealer logs)',
              'PDF reports with your company branding',
              'Priority support',
            ].map((feature, i) => (
              <li key={i} className="flex items-center gap-3 text-gray-300">
                <CheckCircle size={18} className="text-signal-green flex-shrink-0" />
                {feature}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
}
